(() => {
  const SIGNUP_DISMISS_KEY = 'khaos-summer-signup-hidden-until-v1';
  const SIGNUP_JOINED_KEY = 'khaos-summer-signup-joined-v1';
  const DAY_IN_MS = 86400000;

  const pad = (value) => String(Math.max(0, value)).padStart(2, '0');

  const initTypewriterHeadings = (scope = document) => {
    scope.querySelectorAll('[data-khaos-typewriter]:not([data-khaos-typewriter-ready])').forEach((heading) => {
      heading.dataset.khaosTypewriterReady = 'true';
      heading.setAttribute('aria-label', heading.textContent.replace(/\s+/g, ' ').trim());

      const walker = document.createTreeWalker(heading, NodeFilter.SHOW_TEXT);
      const textNodes = [];
      while (walker.nextNode()) textNodes.push(walker.currentNode);

      textNodes.forEach((textNode) => {
        const fragment = document.createDocumentFragment();
        textNode.textContent.split(/(\s+)/).forEach((part) => {
          if (!part) return;
          if (/^\s+$/.test(part)) {
            fragment.appendChild(document.createTextNode(part));
            return;
          }

          const word = document.createElement('span');
          word.className = 'khaos-summer__type-word';
          Array.from(part).forEach((character) => {
            const span = document.createElement('span');
            span.className = 'khaos-summer__type-char';
            span.setAttribute('aria-hidden', 'true');
            span.textContent = character;
            word.appendChild(span);
          });
          fragment.appendChild(word);
        });
        textNode.replaceWith(fragment);
      });

      const characters = Array.from(heading.querySelectorAll('.khaos-summer__type-char'));
      const reverse = heading.dataset.khaosTypewriterDirection === 'reverse';
      characters.forEach((character, index) => {
        character.style.setProperty('--khaos-char-index', reverse ? characters.length - index - 1 : index);
      });

      const reveal = () => window.requestAnimationFrame(() => heading.classList.add('khaos-summer__heading--typing'));
      if (!heading.hasAttribute('data-khaos-typewriter-scroll') || !('IntersectionObserver' in window)) {
        reveal();
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        reveal();
      }, { threshold: 0.35 });
      observer.observe(heading);
    });
  };

  const initHomepageSectionReveals = (scope = document) => {
    if (document.body.dataset.pageType !== 'index') return;

    const sections = scope === document
      ? document.querySelectorAll('#MainContent > .shopify-section:not(.khaos-section-reveal)')
      : scope.matches?.('#MainContent > .shopify-section') ? [scope] : [];

    sections.forEach((section) => {
      section.classList.add('khaos-section-reveal');

      const reveal = () => section.classList.add('khaos-section-reveal--visible');
      if (window.Shopify?.designMode || !('IntersectionObserver' in window)) {
        reveal();
        return;
      }

      const bounds = section.getBoundingClientRect();
      if (bounds.top < window.innerHeight * 0.92) {
        reveal();
        return;
      }

      const observer = new IntersectionObserver((entries) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        reveal();
      }, { threshold: 0.08, rootMargin: '0px 0px -6% 0px' });
      observer.observe(section);
    });
  };

  const readStorage = (key) => {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  };

  const writeStorage = (key, value) => {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {
      // The popup still works when a browser blocks local storage.
    }
  };

  const updateCountdown = (element) => {
    const end = new Date(element.dataset.end || '').getTime();
    if (!Number.isFinite(end)) return;

    const remaining = Math.max(0, end - Date.now());
    const days = Math.floor(remaining / 86400000);
    const hours = Math.floor((remaining % 86400000) / 3600000);
    const minutes = Math.floor((remaining % 3600000) / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);

    element.querySelector('[data-khaos-days]')?.replaceChildren(document.createTextNode(pad(days)));
    element.querySelector('[data-khaos-hours]')?.replaceChildren(document.createTextNode(pad(hours)));
    element.querySelector('[data-khaos-minutes]')?.replaceChildren(document.createTextNode(pad(minutes)));
    element.querySelector('[data-khaos-seconds]')?.replaceChildren(document.createTextNode(pad(seconds)));
  };

  const startCountdowns = () => {
    const countdowns = document.querySelectorAll('[data-khaos-countdown]');
    if (!countdowns.length) return;
    countdowns.forEach(updateCountdown);
    window.setInterval(() => countdowns.forEach(updateCountdown), 1000);
  };

  const findSignupModal = (opener) => {
    const controlledId = opener?.getAttribute('aria-controls');
    if (controlledId) {
      const controlledModal = document.getElementById(controlledId);
      if (controlledModal) return controlledModal;
    }
    return document.querySelector('[data-khaos-signup-modal]');
  };

  const openSignupModal = (modal, opener) => {
    if (!modal) return;
    // Shopify section wrappers can create their own stacking/containing context.
    // Keep the dialog at body level so a sticky header cannot cover or clip it.
    if (modal.parentElement !== document.body) document.body.appendChild(modal);
    window.clearTimeout(modal._khaosAutoTimer);
    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    modal._khaosTrigger = opener || document.activeElement;
    document.body.classList.add('khaos-signup-open');
    window.requestAnimationFrame(() => modal.querySelector('[role="dialog"]')?.focus());
  };

  const closeSignupModal = (modal) => {
    if (!modal) return;
    const dismissalDays = Math.max(1, Number(modal.dataset.khaosSignupDays) || 14);
    writeStorage(SIGNUP_DISMISS_KEY, String(Date.now() + (dismissalDays * DAY_IN_MS)));
    if (modal.querySelector('[data-khaos-signup-success]')) {
      writeStorage(SIGNUP_JOINED_KEY, 'true');
    }
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('khaos-signup-open');
    modal._khaosTrigger?.focus?.();
  };

  const initSignupModals = () => {
    const modal = document.querySelector('[data-khaos-signup-modal]');
    if (!modal) return;

    if (modal.parentElement !== document.body) document.body.appendChild(modal);

    const signupSucceeded = Boolean(modal.querySelector('[data-khaos-signup-success]'));
    const hasFormResponse = signupSucceeded || Boolean(modal.querySelector('[data-khaos-signup-error]'));
    const explicitlyRequested = window.location.hash === '#khaos-summer-signup';

    if (signupSucceeded) writeStorage(SIGNUP_JOINED_KEY, 'true');
    if (hasFormResponse || explicitlyRequested) {
      openSignupModal(modal);
      return;
    }

    if (window.Shopify?.designMode || modal.dataset.khaosSignupAuto !== 'true') return;
    if (readStorage(SIGNUP_JOINED_KEY) === 'true') return;

    const hiddenUntil = Number(readStorage(SIGNUP_DISMISS_KEY));
    if (Number.isFinite(hiddenUntil) && hiddenUntil > Date.now()) return;

    const requestedDelay = Number(modal.dataset.khaosSignupDelay);
    const delay = Math.min(15000, Math.max(0, Number.isFinite(requestedDelay) ? requestedDelay : 4000));
    modal._khaosAutoTimer = window.setTimeout(() => openSignupModal(modal), delay);
  };

  const copyText = async (text) => {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }

    const field = document.createElement('textarea');
    field.value = text;
    field.setAttribute('readonly', '');
    field.style.position = 'fixed';
    field.style.opacity = '0';
    document.body.appendChild(field);
    field.select();
    document.execCommand('copy');
    field.remove();
  };

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-khaos-copy-code]');
    if (!button) return;

    const code = button.dataset.khaosCopyCode;
    const label = button.querySelector('[data-khaos-copy-label]');
    const original = label?.textContent;

    try {
      await copyText(code);
      if (label) label.textContent = 'COPIED';
      window.setTimeout(() => {
        if (label && original) label.textContent = original;
      }, 1800);
    } catch (error) {
      if (label) label.textContent = code;
    }
  });

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-khaos-signup-open]');
    if (opener) {
      event.preventDefault();
      const modal = findSignupModal(opener);
      if (modal) {
        openSignupModal(modal, opener);
      } else if (opener.dataset.khaosSignupFallback) {
        window.location.assign(opener.dataset.khaosSignupFallback);
      }
      return;
    }

    const closer = event.target.closest('[data-khaos-signup-close]');
    if (closer) {
      event.preventDefault();
      closeSignupModal(closer.closest('[data-khaos-signup-modal]'));
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const modal = document.querySelector('[data-khaos-signup-modal]:not([hidden])');
    if (modal) closeSignupModal(modal);
  });

  const initializeCampaign = () => {
    initHomepageSectionReveals();
    initTypewriterHeadings();
    startCountdowns();
    initSignupModals();
  };

  document.addEventListener('shopify:section:load', (event) => {
    initHomepageSectionReveals(event.target);
    initTypewriterHeadings(event.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeCampaign, { once: true });
  } else {
    initializeCampaign();
  }
})();
